/*
    Create XML string here for Para FIS
    Based on the original createFISxml.js but with dynamic discipline support
*/
export function createParaFISxml(headerInfo, data, jurry, setxmlURL) {
    // Helper function to get standardized gender value
    const getGenderValue = (row) => {
        const genderColumns = ['sex', 'Gender', 'gender', 'Sex']
        for (const col of genderColumns) {
            if (row[col]) {
                let genderValue = row[col].toLowerCase().trim()
                // Convert to standard format
                if (genderValue === 'male' || genderValue === 'm') {
                    return 'M'
                } else if (genderValue === 'female' || genderValue === 'f') {
                    return 'W'
                } else if (genderValue === 'team' || genderValue === 'a') {
                    return 'A'
                } else {
                    return genderValue.toUpperCase()
                }
            }
        }
        return null
    }

    // Determine the prefix based on the sector
    const sector = headerInfo.sector || "CC";
    let prefix = "CC";

    if (sector === "PCC") prefix = "PCC";
    else if (sector === "PSB") prefix = "PSB";
    else if (sector === "PAL") prefix = "PAL";
    
    // Common tags that use the prefix
    const tags = {
        race: `${prefix}_race`,
        raceinfo: `${prefix}_raceinfo`,
        classified: `${prefix}_classified`,
        notclassified: `${prefix}_notclassified`,
        ranked: `${prefix}_ranked`,
        notranked: `${prefix}_notranked`,
        result: `${prefix}_result`
    };

    // Helper to determine Categorycode based on Sportclass (FIS Para specification)
    const getCategoryCode = (sportClass) => {
        if (!sportClass) return "";
        const sc = sportClass.toUpperCase();

        // FIS Para Category Codes:
        // SI (Sitting): LW10, LW11, LW12 (including decimals)
        // ST (Standing): LW2-LW9, LL1, LL2, UL (standing classes)
        // VI (Vision Impaired): B1-B3, NS1-NS3
        // LL1 (Lower Limb 1): LL1
        // LL2 (Lower Limb 2): LL2
        // UL (Upper Limb): UL

        // Check for sitting classes (LW10, LW11, LW12 with possible decimals)
        if (sc.match(/^LW1[0-2]/)) return "SI";

        // Check for specific standing subclasses
        if (sc === "LL1") return "LL1";
        if (sc === "LL2") return "LL2";
        if (sc === "UL") return "UL";

        // Check for general standing classes (other LW classes)
        if (sc.startsWith("LW")) return "ST";

        // Check for vision impaired classes
        if (sc.startsWith("B") || sc.startsWith("NS") || sc === "VI") return "VI";

        return ""; // Default fallback
    };

    // Helper to split guide name
    const getGuideName = (fullName) => {
        if (!fullName) return { first: "", last: "" };
        const parts = fullName.split(" ");
        if (parts.length === 1) return { first: "", last: parts[0] };
        return {
            first: parts.slice(0, -1).join(" "),
            last: parts[parts.length - 1]
        };
    };

    function formatXml(sourceXml) {
        sourceXml = sourceXml.replace(/\t/g, '');
        var xmlDoc = new DOMParser().parseFromString(sourceXml, 'application/xml');
        var xsltDoc = new DOMParser().parseFromString([
            // describes how we want to modify the XML - indent everything
            '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform" version="1.0">',
            '  <xsl:strip-space elements="*"/>',
            '  <xsl:template match="para[content-style][not(text())]">', // change to just text() to strip space in text nodes
            '    <xsl:value-of select="normalize-space(.)"/>',
            '  </xsl:template>',
            '  <xsl:template match="node()|@*">',
            '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
            '  </xsl:template>',
            '  <xsl:output indent="yes"/>',
            '</xsl:stylesheet>',
        ].join('\n'), 'application/xml');
    
        var xsltProcessor = new XSLTProcessor();    
        xsltProcessor.importStylesheet(xsltDoc);
        var resultDoc = xsltProcessor.transformToDocument(xmlDoc);
        var resultXml = new XMLSerializer().serializeToString(resultDoc);
        return resultXml;
    }
    
    const makeHeaderString = () => {
        const dateNeeded = headerInfo.raceDate.split("-");
        let season = dateNeeded[0]

        // if the season is grater then june (06) then put the season + 1
        if (parseInt(dateNeeded[1]) > 6) {
            season = parseInt(season) + 1
        }

        // Base header string
        let xmlString = `\t<Raceheader Sector="${sector}" Sex="${headerInfo.sex || null}">
    \t\t<Season>${season || ""}</Season>
    \t\t<Codex>${
        headerInfo.codex || ""
        }</Codex>
    \t\t<Nation>${
        headerInfo.nation || ""
        }</Nation>
    \t\t<Discipline>${
        headerInfo.discipline || ""
        }</Discipline>
    \t\t<Category>${
        headerInfo.category || ""
        }</Category>
    \t\t<Racedate>
    \t\t\t<Day>${
        dateNeeded[2] || ""
        }</Day>
    \t\t\t<Month>${
        dateNeeded[1] || ""
        }</Month>
    \t\t\t<Year>${dateNeeded[0] || ""}</Year>
    \t\t</Racedate>
    \t\t<Coordinator>${headerInfo.coordinator || ""}</Coordinator>
    \t\t<HD>${headerInfo.hd || ""}</HD>
    \t\t<MC>${headerInfo.mc || ""}</MC>
    \t\t<TC>${headerInfo.tc || ""}</TC>
    \t\t<Laplength>${headerInfo.lapLength || ""}</Laplength>
    \t\t<Lapnumber>${headerInfo.lapNumber || ""}</Lapnumber>
    \t\t<Name>${headerInfo.eventName || ""}</Name>
    \t\t<CC_format>${headerInfo.format || ""}</CC_format>
    \t\t<Eventname>${headerInfo.eventName || ""}</Eventname>
    \t\t<Place>${headerInfo.place || ""}</Place>
    \t\t<Type>${headerInfo.type || ""}</Type>
    \t\t<Tempunit>${headerInfo.tempunit || ""}</Tempunit>
    \t\t<Longunit>${headerInfo.longunit || ""}</Longunit>
    \t\t<Speedunit>${headerInfo.speedunit || ""}</Speedunit>
    \t\t<Windunit>${headerInfo.windunit || ""}</Windunit>
    \t</Raceheader>`;
        return xmlString;
    };

    const makeRaceInfoString = () => {
        const makeJurryString = (Jurry) => {
        const xmlString = `\t\t<Jury Function="${Jurry["Function"]}">
    \t\t\t\t<Lastname>${Jurry["LastName"]}</Lastname>
    \t\t\t\t<Firstname>${Jurry["FirstName"]}</Firstname>
    \t\t\t\t<nation>${Jurry["Nation"]}</nation>
    \t\t\t</Jury>`;
        return xmlString;
        };

        const jurryStrings = [];
        jurry.forEach((jurry) => {
        const xmlString = makeJurryString(jurry);
        jurryStrings.push(xmlString);
        });

        const xmlString = `\t\t<${tags.raceinfo}>
        ${jurryStrings.join("\n")}${jurryStrings.length > 0 ? "\n" : ""}
    \t\t\t<Runinfo No="${headerInfo.no || ""}">
    \t\t\t\t<Starttime>${headerInfo.startTime || ""}</Starttime>
    \t\t\t</Runinfo>
    \t\t\t<Usedfislist>${headerInfo.usedFISList || ""}</Usedfislist>
    \t\t\t<Appliedpenalty>${headerInfo.penalty}</Appliedpenalty>
    \t\t\t<Calculatedpenalty>${headerInfo.calculatedPenalty}</Calculatedpenalty>
    \t\t\t<Fvalue>${headerInfo.suggestedFFactor || ""}</Fvalue>
    ${headerInfo.timingBy ? (`\t\t\t<Timingby>${headerInfo.timingBy}`):("")}</Timingby>
    ${headerInfo.dataProcessingBy ? (`\t\t\t<Dataprocessingby>${headerInfo.dataProcessingBy}`):("")}</Dataprocessingby>
    ${headerInfo.softWareCompany ? (`\t\t\t<Softwarecompany>${headerInfo.softWareCompany}`):("")}</Softwarecompany>
    \t\t</${tags.raceinfo}>`;

        return xmlString;
    };

    // startlists for indidual races
    const makeRacersStringIndStartList = () => {
        // helper function to make the XML for a single racer
        const makeSingleRacerString = (row, index) => {
            const sportClass = row["sportClass"] || row["Classification"] || "";
            const factor = row["factor"] || row["Time Factor"] ? (parseFloat(row["factor"] || row["Time Factor"]) / 100).toFixed(3) : ""; // Convert 100 to 1.00, 86 to 0.86
            
            let guideString = "";
            const guideFIScode1 = row["guideFIScode"] || row["Guide FIS Licence"] || "";
            const guideNameRaw1 = row["guideName"] || row["Guide"] || "";
            if (guideFIScode1 || guideNameRaw1) {
                const guideName = getGuideName(guideNameRaw1);
                guideString = `\t\t\t\t<Guide>
    \t\t\t\t\t<Fiscode>${guideFIScode1}</Fiscode>
    \t\t\t\t\t<Lastname>${guideName.last}</Lastname>
    \t\t\t\t\t<Firstname>${guideName.first}</Firstname>
    \t\t\t\t\t<Nation>${row["Nation"] || row["Country"] || ""}</Nation>
    \t\t\t\t</Guide>`;
            }

            let xmlString = `\t\t\t<${tags.ranked} Status="${row["status"]}">
    \t\t\t\t<Bib>${
            row["Racer #"] || ""
            }</Bib>
    \t\t\t\t<Rank>${
            (index + 1) || ""
            }</Rank>
    \t\t\t\t<Competitor>
    \t\t\t\t\t<Fiscode>${
            row["FIS Licence"] || ""
            }</Fiscode>
    \t\t\t\t\t<Lastname>${
            row["lastname"] || ""
            }</Lastname>
    \t\t\t\t\t<Firstname>${
            row["firstname"] || ""
            }</Firstname>
    \t\t\t\t\t<Sex>${
            getGenderValue(row) || headerInfo.sex || "M"
        }</Sex>
    \t\t\t\t\t<Nation>${
            row["Nation"] || ""
            }</Nation>
    \t\t\t\t\t<Yearofbirth>${
            row["YoB"] || ""
            }</Yearofbirth>
    \t\t\t\t\t<Sportclass>${sportClass}</Sportclass>
    \t\t\t\t\t<Factor>${factor}</Factor>
    \t\t\t\t</Competitor>
    ${guideString}
    \t\t\t</${tags.ranked}>`;
            return xmlString;
        };

        // make xml string for each racer
        const xmlStrings = [];
        data.forEach((row, index) => {
            if (row["status"] === "") {
            row["status"] = "QLF";
            }
            // if the racer does not have a first or last name dont do this
            if (
            row["firstname"] !== undefined &&
            row["lastname"] !== undefined &&
            row["status"] === "QLF"
            ) {
            const xmlString = makeSingleRacerString(row, index);
            xmlStrings.push(xmlString);
            }
        });

        // make xml string for all racers
        const xmlString = `\t\t<${tags.classified}>
        ${xmlStrings.join("\n")}
    \t\t</${tags.classified}>`;


        return xmlString;
    };

    // results for individual distance and sprint qualification 
    const makeRacersString = () => {
        // helper function to make the XML for a single racer
        const makeSingleRacerString = (row) => {
            const sportClass = row["sportClass"] || row["Classification"] || "";
            const factor = row["factor"] || row["Time Factor"] ? (parseFloat(row["factor"] || row["Time Factor"]) / 100).toFixed(3) : "";
            const categoryCode = getCategoryCode(sportClass);
            
            let guideString = "";
            const guideFIScode = row["guideFIScode"] || row["Guide FIS Licence"] || "";
            const guideNameRaw = row["guideName"] || row["Guide"] || "";
            if (guideFIScode || guideNameRaw) {
                const guideName = getGuideName(guideNameRaw);
                guideString = `\t\t\t\t<Guide>
    \t\t\t\t\t<Fiscode>${guideFIScode}</Fiscode>
    \t\t\t\t\t<Lastname>${guideName.last}</Lastname>
    \t\t\t\t\t<Firstname>${guideName.first}</Firstname>
    \t\t\t\t\t<Nation>${row["Nation"] || row["Country"] || ""}</Nation>
    \t\t\t\t</Guide>`;
            }

        let xmlString = `\t\t\t<${tags.ranked} Status="${row["status"]}">
    \t\t\t\t<Rank>${
            row["rank"] === undefined || row["rank"] === "" ? "" : row["rank"]
        }</Rank>
    \t\t\t\t<Bib>${
            row["Racer #"] === undefined || row["Racer #"] === ""
            ? ""
            : row["Racer #"]
        }</Bib>
    \t\t\t\t<Competitor>
    \t\t\t\t\t<Fiscode>${
            row["FIS Licence"] || row["FIScode"] || ""
            }</Fiscode>
    \t\t\t\t\t<Lastname>${
            row["lastname"] || ""
            }</Lastname>
    \t\t\t\t\t<Firstname>${
            row["firstname"] || ""
            }</Firstname>
    \t\t\t\t\t<Sex>${
            getGenderValue(row) || headerInfo.sex || "M"
        }</Sex>
    \t\t\t\t\t<Nation>${
            row["Nation"] || row["Country"] || ""
            }</Nation>
    \t\t\t\t\t<Yearofbirth>${
            row["YoB"] || row["YOB"] || ""
            }</Yearofbirth>
    \t\t\t\t\t<Sportclass>${sportClass}</Sportclass>
    \t\t\t\t\t<Factor>${factor}</Factor>
    \t\t\t\t</Competitor>
    ${guideString}
    \t\t\t\t<${tags.result}>
    \t\t\t\t\t<TotalTime>${
            row["totalTime"] || row["Time"] || row["time"] || ""
        }</TotalTime>
                    ${(row["rawTime"] || row["Raw Time"]) ? `<Totalrealtime>${row["rawTime"] || row["Raw Time"]}</Totalrealtime>` : ""}
    \t\t\t\t\t<RacePoints>${
            row["Race Points"] || ""
        }</RacePoints>
                    <Categorycode>${categoryCode}</Categorycode>
    \t\t\t\t</${tags.result}>
    \t\t\t</${tags.ranked}>`;
        return xmlString;
        };

        const makeSingleRacerStringNotQLF = (row) => {
            const sportClass = row["sportClass"] || row["Classification"] || "";
            const factor = row["factor"] || row["Time Factor"] ? (parseFloat(row["factor"] || row["Time Factor"]) / 100).toFixed(3) : "";
            const categoryCode = getCategoryCode(sportClass);
            
            let guideString = "";
            const guideFIScode3 = row["guideFIScode"] || row["Guide FIS Licence"] || "";
            const guideNameRaw3 = row["guideName"] || row["Guide"] || "";
            if (guideFIScode3 || guideNameRaw3) {
                const guideName = getGuideName(guideNameRaw3);
                guideString = `\t\t\t\t<Guide>
    \t\t\t\t\t<Fiscode>${guideFIScode3}</Fiscode>
    \t\t\t\t\t<Lastname>${guideName.last}</Lastname>
    \t\t\t\t\t<Firstname>${guideName.first}</Firstname>
    \t\t\t\t\t<Nation>${row["Nation"] || row["Country"] || ""}</Nation>
    \t\t\t\t</Guide>`;
            }


        let xmlString = `\t\t\t<${tags.notranked} Status="${row["status"]}">
    \t\t\t\t<Bib>${
            row["Racer #"] || ""
        }</Bib>
    \t\t\t\t<Competitor>
    \t\t\t\t\t<Fiscode>${
            row["FIS Licence"] || row["FIScode"] || ""
        }</Fiscode>
    \t\t\t\t\t<Lastname>${
            row["lastname"] || ""
        }</Lastname>
    \t\t\t\t\t<Firstname>${
            row["firstname"] || ""
        }</Firstname>
    \t\t\t\t\t<Sex>${
            getGenderValue(row) || headerInfo.sex || "M"
        }</Sex>
    \t\t\t\t\t<Nation>${
            row["Nation"] || row["Country"] || ""
        }</Nation>
    \t\t\t\t\t<Yearofbirth>${
            row["YoB"] || row["YOB"] || ""
        }</Yearofbirth>
    \t\t\t\t\t<Sportclass>${sportClass}</Sportclass>
    \t\t\t\t\t<Factor>${factor}</Factor>
    \t\t\t\t</Competitor>
    ${guideString}
    \t\t\t\t<${tags.result}>
    \t\t\t\t\t<Categorycode>${categoryCode}</Categorycode>
    \t\t\t\t</${tags.result}>
    \t\t\t</${tags.notranked}>`;
        return xmlString;
        };

        // make xml string for each racer
        const xmlStrings = [];
        data.forEach((row) => {
        if (row["status"] === "") {
            row["status"] = "QLF";
        }
        // if the racer does not have a first or last name dont do this
        if (
            row["firstname"] !== undefined &&
            row["lastname"] !== undefined &&
            row["status"] === "QLF"
        ) {
            const xmlString = makeSingleRacerString(row);
            xmlStrings.push(xmlString);
        }
        });

        // make xml string for all racers
        const xmlString = `\t\t<${tags.classified}>
        ${xmlStrings.join("\n")}
    \t\t</${tags.classified}>`;

        // clear the xmlStrings array
        xmlStrings.length = 0;

        data.forEach((row) => {
        if (
            row["firstname"] !== undefined &&
            row["lastname"] !== undefined &&
            row["status"] !== "QLF"
        ) {
            const xmlString = makeSingleRacerStringNotQLF(row);
            xmlStrings.push(xmlString);
        }
        });

        const xmlString2 = `\n\n\t\t<${tags.notclassified}>
        ${xmlStrings.join("\n")}
    \t\t</${tags.notclassified}>`;

        return xmlString + "\n" + xmlString2;
    };

    // xml string for header
    const headerString = makeHeaderString();
    const raceInfoString = makeRaceInfoString();
    let racersString = ''

    if(headerInfo.type==="Startlist"){
        // this is for STARTLIST
        if(headerInfo.format === "individualDistance" || headerInfo.format === "sprintQualification" || headerInfo.format === "sprintFinal"){
            racersString = makeRacersStringIndStartList();
        }else if(headerInfo.format === "teamSprint"){
            // TODO: Implement Team Sprint for Para if needed
            racersString = makeRacersStringIndStartList(); // Fallback for now
        }else if(headerInfo.format === "team"){
            // TODO: Implement Team for Para if needed
            racersString = makeRacersStringIndStartList(); // Fallback for now
        }else{
            console.warn("headerInfo.format is not valid");
        }
    }else{
        // this is for RESULTS
        if(headerInfo.format === "individualDistance" || headerInfo.format === "sprintQualification"){
            racersString = makeRacersString();
        }else if(headerInfo.format === "sprintFinal"){
            // TODO: Implement Sprint Final for Para
             racersString = makeRacersString(); // Fallback for now
        }else if(headerInfo.format === "teamSprint"){
             racersString = makeRacersString(); // Fallback for now
        }else if(headerInfo.format === "team"){
             racersString = makeRacersString(); // Fallback for now
        }else{
             console.warn("headerInfo.format is not valid");
        }
    }

    // compile all strings into one
    var xmlString =
        `<?xml version="1.0" encoding="UTF-8"?>\n<!-- Created by Zone4 Systems www.zone4.ca -->\n<Fisresults>\n` +
        headerString +
        "\n" +
        `\t<${tags.race}>\n` +
        raceInfoString +
        "\n" +
        racersString +
        "\n" +
        `\t</${tags.race}>\n` +
        "</Fisresults>";

    xmlString = formatXml(xmlString);
    const xmlBlob = new Blob([xmlString], { type: "text/xml" });
    const xmlUrl = URL.createObjectURL(xmlBlob);

    setxmlURL(xmlUrl);
};
